import { useCallback, useEffect, useRef, useState } from "react";
import { scrollEdges, type ScrollEdges } from "../../lib/carousel-math";

const INITIAL: ScrollEdges = { atStart: true, atEnd: true, overflowing: false };

/**
 * Tracks whether a horizontal scroller sits at its start or end and whether it overflows at all.
 * Re-measures on scroll, on resize of the scroller or of its content, and when `watch` changes.
 */
export function useScrollEdges<T extends HTMLElement>(watch?: unknown) {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState<ScrollEdges>(INITIAL);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = scrollEdges(el.scrollLeft, el.scrollWidth, el.clientWidth);
    setEdges((prev) =>
      prev.atStart === next.atStart &&
      prev.atEnd === next.atEnd &&
      prev.overflowing === next.overflowing
        ? prev
        : next,
    );
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(el);
      if (el.firstElementChild) observer.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [measure, watch]);

  return { ref, edges, measure };
}
